import { Stack, ToggleButtonGroup, Typography, ToggleButton, Badge, Button } from '@mui/material';
import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import CalendarFilterField from './CalendarFilterField';
import LocationFilterField from './LocationFilterField';
import AppointmentModal from '../AppointmentModal';
import CalendarNavigation from '../Calendar/CalendarNavigation';
import Toolbar from 'FRONTEND/components/Calendar/Toolbar';
import { resetBookingForm } from 'FRONTEND/reducers/bookingSlice';
import { changeCalendarView, goToGivenExpertForm, goToGivenSlotForm, goToLocationForm, toggleAppointmentModal } from 'FRONTEND/reducers/calendarSlice';
import { RootState } from 'FRONTEND/reducers/types';
import { selectHasRight } from 'FRONTEND/reducers/userSlice';
import { selectAppointmentsWithActionNeededCount } from 'FRONTEND/selectors/appointmentSelectors';
import { FormTypes } from 'FRONTEND/utils/constants';
import i18n from 'FRONTEND/utils/i18n';
import { RIGHTS } from 'FRONTEND/utils/rights';
import { CALENDAR_VIEWS } from 'LEGACY/constants';


type CalendarToolbarProps = {
  isLoading?: boolean;
  // props from react-big-calendar's Toolbar
  views: string[];
  view: string;
  onView: (view: string) => void;
  onNavigate: (action: string) => void;
  date: Date;
  localizer: { format: (date: Date, format: string) => string; }; // Replace with actual type if available
}


const CalendarToolbar = ({
  isLoading,
  ...toolbarProps
}: CalendarToolbarProps) => {
  const dispatch = useDispatch();
  const isSmall = useSelector((state: RootState) => state.app.isSmall);
  const hasAppointmentLocationRights = useSelector((state) => selectHasRight(state, RIGHTS.APPOINTMENTS_LOCATIONS));
  const hasManagementRight = useSelector((state) => selectHasRight(state, RIGHTS.CALENDAR_MANAGEMENT));
  const currentUser = useSelector((state: RootState) => state.user);
  const { views, view, onView } = toolbarProps;
  const calendarView = useSelector((state: RootState) => state.calendar.view);
  const actionNeededCount = useSelector(selectAppointmentsWithActionNeededCount);
  useEffect(() => {
    // Sync react-big-calendar view with our redux state on mount
    // Not all our views are supported by react-big-calendar (planning)
    if (views.includes(calendarView) && view !== calendarView) {
      onView(calendarView);
    }
  }, [calendarView]);

  const handleViewChange = (event, newView) => {
    if (newView !== null) {
      dispatch(changeCalendarView({ view: newView }));
    }
  };

  // Appointment Modal Management
  const isAppointmentModalOpen = useSelector((state: RootState) => state.calendar.isAppointmentModalOpen);
  const currentForm = useSelector((state: RootState) => state.booking.formType);

  const handleOpenAppointmentModal = () => {
    // Part of the booking state is restored from the localStorage, so a fresh form
    // would otherwise open with the patient, slot and reason of a previous session
    // and count as already started. The return path from patient creation reopens
    // the modal through goBackToForm instead, and keeps what was entered.
    dispatch(resetBookingForm());
    if (currentForm === FormTypes.LOCATION_FORM && hasAppointmentLocationRights) {
      dispatch(goToLocationForm({}));
    } else if (hasManagementRight) {
      dispatch(goToGivenSlotForm());
    } else {
      dispatch(goToGivenExpertForm({ id: currentUser?.id }));
    }
    dispatch(toggleAppointmentModal({ isOpen: true }));
  };

  return (
    <Stack spacing={1}>
      <Toolbar isMenu={isSmall} title={i18n._('Calendar controls')} isLoading={isLoading}>
        {/* TODO: Add new appointment button here when ready */}
        <Button variant='contained' onClick={handleOpenAppointmentModal}>
          {i18n._('New Appointment')}
        </Button>
        <ToggleButtonGroup
          value={calendarView}
          onChange={handleViewChange}
          exclusive
          sx={{
            height: 40,
            position: isSmall ? 'static' : 'absolute',
            left: isSmall ? '0' : '50%',
            transform: isSmall ? 'none' : 'translateX(-50%)',
            '& .MuiToggleButton-root': {
              padding: '2px 8px',
            },
          }}
        >
          <ToggleButton value='day'>
            <Typography variant='body2'>{i18n._('Day')}</Typography>
          </ToggleButton>
          <ToggleButton value='week'>
            <Typography variant='body2'>{i18n._('Week')}</Typography>
          </ToggleButton>
          <ToggleButton value='planning'>
            <Badge badgeContent={actionNeededCount} color='secondary'>
              <Typography variant='body2'>{i18n._('Planning')}</Typography>
            </Badge>
          </ToggleButton>
        </ToggleButtonGroup>
        <Stack direction='row' justifyContent='center' spacing={1}>
          <CalendarFilterField isLoading={isLoading} />
          <LocationFilterField isLoading={isLoading} />
        </Stack>
      </Toolbar>
      {calendarView !== CALENDAR_VIEWS.PLANNING && (
        <CalendarNavigation
          onNavigate={toolbarProps.onNavigate}
          date={toolbarProps.date}
          localizer={toolbarProps.localizer}
          selectedView={calendarView}
          isSmall={isSmall}
        />
      )}
      <AppointmentModal
        open={isAppointmentModalOpen}
        onClose={() => dispatch(toggleAppointmentModal({ isOpen: false }))}
        hasAppointmentLocationRights={hasAppointmentLocationRights}
        hasManagementRight={hasManagementRight}
        currentUser={currentUser}
      />
    </Stack>
  );
};

export default CalendarToolbar;
